import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Zap, Building2, Rocket, Star, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const PLANS = [
  { key: "starter",      name: "Starter",      monthly: 99,   yearly: 950,   credits: 20000,    icon: Rocket,     features: ["20,000 credits/mo", "Rollover 3 months", "All validators unified", "REST API + MCP", "Email support"] },
  { key: "growth",       name: "Growth",       monthly: 299,  yearly: 2870,  credits: 85000,    popular: true, icon: Zap,        features: ["85,000 credits/mo", "Rollover 3 months", "$0.0035 per credit", "Bulk CSV workflows", "Priority support"] },
  { key: "professional", name: "Professional", monthly: 799,  yearly: 7670,  credits: 250000,   icon: Star,       features: ["250,000 credits/mo", "Rollover 3 months", "$0.0032 per credit", "Team seats", "SLA + audit logs"] },
  { key: "enterprise",   name: "Enterprise",   monthly: 1999, yearly: 19190, credits: 1000000,  icon: Building2,  features: ["1,000,000 credits/mo", "$0.0020 per credit", "SSO + SCIM", "Dedicated Slack channel", "Custom carrier feeds"] },
];

export default function Pricing() {
  const [interval, setInterval] = useState("monthly");
  const [creditsSlider, setCreditsSlider] = useState(50000);
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [stripeLive, setStripeLive] = useState(true); // optimistic
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistPlan, setWaitlistPlan] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/config").then(({ data }) => setStripeLive(!!data.stripe_live)).catch(() => {});
  }, []);

  const upgrade = async (plan) => {
    if (!stripeLive) {
      setWaitlistPlan(plan);
      setWaitlistEmail(user?.email || "");
      return;
    }
    if (!user) { nav("/register"); return; }
    setLoading(plan);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { plan, origin_url, interval });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Could not start checkout");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(null); }
  };

  const joinWaitlist = async () => {
    if (!waitlistEmail) { toast.error("Enter an email"); return; }
    setWaitlistLoading(true);
    try {
      await api.post("/waitlist/join", { email: waitlistEmail, plan: waitlistPlan, interval });
      setWaitlistJoined(true);
      toast.success("You're on the list — we'll email you the second paid plans open");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWaitlistLoading(false); }
  };

  const estCost = (creditsSlider * 0.0035).toFixed(2);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {!stripeLive && (
        <div className="mb-8 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-slate-900/60 p-4 sm:p-5 flex items-start gap-3" data-testid="prelaunch-banner">
          <Bell className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="text-slate-100 font-medium">Paid plans open in the next few days.</p>
            <p className="text-slate-400 mt-0.5">Free tier is live now — 500 credits + <span className="text-cyan-signal font-mono">LAUNCH50</span> for another 500 bonus. Drop your email on any plan below and we'll notify you the moment paid checkout goes live.</p>
          </div>
        </div>
      )}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Pricing</span>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight">One credit. Any validator.</h1>
        <p className="mt-4 text-slate-400">Phone, email or IP — every verification is a single credit. Unused credits roll over three months.</p>
      </div>

      {/* Interval switch */}
      <div className="flex justify-center mb-10" data-testid="pricing-interval-switch">
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-slate-800 bg-slate-900/60">
          <button
            data-testid="btn-interval-monthly"
            onClick={() => setInterval("monthly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${interval === "monthly" ? "bg-cyan-signal text-slate-950" : "text-slate-400 hover:text-white"}`}
          >Monthly</button>
          <button
            data-testid="btn-interval-yearly"
            onClick={() => setInterval("yearly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 ${interval === "yearly" ? "bg-cyan-signal text-slate-950" : "text-slate-400 hover:text-white"}`}
          >
            Yearly
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${interval === "yearly" ? "bg-slate-950/20 text-slate-950" : "bg-emerald-500/10 text-emerald-400"}`}>SAVE 20%</span>
          </button>
        </div>
      </div>

      {/* Credit calculator */}
      <div className="max-w-2xl mx-auto rounded-xl border border-slate-800 bg-slate-900/60 p-6 mb-14" data-testid="credit-calculator">
        <div className="flex justify-between items-baseline mb-3">
          <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Credit estimator</span>
          <span className="font-mono text-cyan-signal text-lg">{creditsSlider.toLocaleString()} credits ≈ ${estCost}/mo</span>
        </div>
        <input
          data-testid="credit-calculator-slider"
          type="range"
          min="5000"
          max="500000"
          step="5000"
          value={creditsSlider}
          onChange={(e) => setCreditsSlider(Number(e.target.value))}
          className="w-full accent-cyan-500 h-2"
        />
        <div className="flex justify-between text-xs font-mono text-slate-500 mt-2">
          <span>5k</span><span>100k</span><span>250k</span><span>500k</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((p) => {
          const Icon = p.icon;
          const current = user?.plan === p.key;
          const price = interval === "monthly" ? p.monthly : Math.round(p.yearly / 12);
          const rawYearly = p.yearly;
          const savings = (p.monthly * 12) - rawYearly;
          return (
            <div
              key={p.key}
              data-testid={`pricing-plan-${p.key}`}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                p.popular
                  ? "border-cyan-signal/40 bg-gradient-to-br from-slate-900 to-cyan-950/20 shadow-[0_0_40px_-16px_rgba(0,240,255,0.35)]"
                  : "border-slate-800 bg-slate-900/40"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-6 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-cyan-signal text-slate-950 rounded">Most popular</span>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-cyan-signal" />
                <span className="font-display font-semibold text-lg">{p.name}</span>
              </div>
              <div className="mb-1 flex items-baseline gap-1.5">
                <span className="text-4xl font-display font-bold tracking-tight">${price.toLocaleString()}</span>
                <span className="text-sm text-slate-500 font-mono">/mo</span>
              </div>
              <div className="text-xs font-mono text-slate-500 min-h-[16px]">
                {interval === "yearly" ? (
                  <span>${rawYearly.toLocaleString()} billed yearly · <span className="text-emerald-400">save ${savings.toLocaleString()}</span></span>
                ) : (
                  <>{p.credits.toLocaleString()} credits</>
                )}
              </div>
              <ul className="my-6 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`btn-select-plan-${p.key}`}
                onClick={() => upgrade(p.key)}
                disabled={current || loading === p.key}
                className={
                  p.popular
                    ? "bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold w-full"
                    : "bg-slate-800 text-slate-100 hover:bg-slate-700 w-full"
                }
              >
                {current ? "Current plan" : loading === p.key ? "Redirecting…" : !stripeLive ? "Notify me →" : user ? "Choose plan →" : "Get started →"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-14 text-center text-sm text-slate-400 max-w-xl mx-auto">
        Need a custom volume, on-prem, or private carrier feed? <a href="mailto:team@thegoodguys.app" className="text-cyan-signal hover:underline">Talk to sales →</a>
      </div>

      {waitlistPlan && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !waitlistLoading && setWaitlistPlan(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 sm:p-8" data-testid="waitlist-modal">
            {waitlistJoined ? (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4"><Check className="w-6 h-6 text-emerald-400" /></div>
                <h3 className="font-display font-bold text-xl">You're in.</h3>
                <p className="text-sm text-slate-400 mt-2">We'll email you the moment paid checkout opens — usually 1–2 business days.</p>
                <Button className="mt-6 w-full bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold" onClick={() => { setWaitlistPlan(null); setWaitlistJoined(false); }}>Nice</Button>
              </div>
            ) : (
              <>
                <Bell className="w-6 h-6 text-cyan-signal mb-3" />
                <h3 className="font-display font-bold text-xl">Notify me for {waitlistPlan}.</h3>
                <p className="text-sm text-slate-400 mt-1">Paid checkout goes live in a couple days once Stripe verifies. You'll be first in line.</p>
                <Input
                  data-testid="waitlist-email-input"
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-5 bg-slate-950 border-slate-800 h-11"
                />
                <div className="mt-3 flex gap-2">
                  <Button variant="ghost" onClick={() => setWaitlistPlan(null)} disabled={waitlistLoading} className="flex-1">Cancel</Button>
                  <Button data-testid="btn-waitlist-submit" onClick={joinWaitlist} disabled={waitlistLoading} className="flex-1 bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">
                    {waitlistLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add me to the list"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
